class VNodeRenderer {
    constructor(h, options = {}) {
        this.h = h;

        this.options = {
            sourcepos: false,
            safe: false,
            components: {},
            ...options,
        };
    }

    render(ast) {
        const walker = ast.walker();
        const buffer = [];
        let event, type;

        while ((event = walker.next())) {
            type = event.node.type;

            if (this[type] && event.entering) {
                buffer.push(this[type](event.node, walker));
            }
        }

        return this.h(
            "div",
            {
                class: "markdown-content",
            },
            buffer
        );
    }

    children(walker) {
        let event, type;

        const buffer = [];

        while ((event = walker.next()) && event.entering) {
            type = event.node.type;

            if (this[type]) {
                buffer.push(this[type](event.node, walker));
            }
        }

        return buffer;
    }

    /* Nodes */

    link(node, walker) {
        const attrs = this.attrs(node);

        if (node.title) {
            attrs.title = node.title;
        }

        attrs.href = node.destination;

        return this.h(
            this.options.components.link || "a",
            {
                attrs: attrs,
            },
            this.children(walker)
        );
    }

    image(node, walker) {
        const attrs = [];

        if (node.firstChild) {
            let event,
                alt = "";

            while ((event = walker.next()) && event.node.type !== "image") {
                if (event.node.type === "text") alt += event.node.literal;
            }

            attrs.push({ alt: alt });
        }

        attrs.push({ src: node.destination });

        return this.h("img", {
            attrs: attrs,
        });
    }

    paragraph(node, walker) {
        const grandparent = node.parent.parent;
        const attrs = this.attrs(node);

        if (grandparent !== null && grandparent.type === "list") {
            if (grandparent.listTight) {
                // Move child nodes up and remove self
                while (node.firstChild) {
                    node.insertBefore(node.firstChild);
                }
                node.unlink();
                return;
            }
        }

        return this.h(
            "p",
            {
                attrs: attrs,
            },
            this.children(walker)
        );
    }

    text(node) {
        return node.literal;
    }

    softbreak() {
        return "\n";
    }

    linebreak() {
        this.h("br");
    }

    emph(node, walker) {
        return this.h("em", this.children(walker));
    }

    strong(node, walker) {
        return this.h("strong", this.children(walker));
    }

    heading(node, walker) {
        const tagname = "h" + node.level;
        const attrs = this.attrs(node);

        return this.h(
            tagname,
            {
                attrs: attrs,
            },
            this.children(walker)
        );
    }

    code(node) {
        return this.h("code", node.literal);
    }

    code_block(node) {
        const info_words = node.info ? node.info.split(/\s+/) : [];
        const attrs = this.attrs(node);
        const classes = [];

        if (info_words.length > 0 && info_words[0].length > 0) {
            classes.push("language-" + info_words[0]);
        }

        return this.h("pre", [
            this.h(
                "code",
                {
                    class: classes,
                    attrs: attrs,
                },
                node.literal
            ),
        ]);
    }

    thematic_break(node) {
        return this.h("hr", {
            attrs: this.attrs(node),
        });
    }

    block_quote(node, walker) {
        const attrs = this.attrs(node);

        return this.h(
            "blockquote",
            {
                attrs: attrs,
            },
            this.children(walker)
        );
    }

    list(node, walker) {
        const tagname = node.listType === "bullet" ? "ul" : "ol";
        const attrs = this.attrs(node);
        const start = node.listStart;

        if (start !== null && start !== 1) {
            attrs.start = start.toString();
        }

        return this.h(
            tagname,
            {
                attrs: attrs,
            },
            this.children(walker)
        );
    }

    item(node, walker) {
        const attrs = this.attrs(node);

        return this.h(
            "li",
            {
                attrs: attrs,
            },
            this.children(walker)
        );
    }

    html_inline(node) {
        node.unlink();
    }

    html_block(node) {
        node.unlink();
    }

    /* Helpers */

    attrs(node) {
        const att = {};

        if (this.options.sourcepos) {
            const pos = node.sourcepos;

            if (pos) {
                att["data-sourcepos"] = String(pos[0][0]) + ":" + String(pos[0][1]) + "-" + String(pos[1][0]) + ":" + String(pos[1][1]);
            }
        }

        return att;
    }

    isAbsoluteUrl(url) {
        if (typeof url !== "string") {
            throw new TypeError(`Expected a \`string\`, got \`${typeof url}\``);
        }

        // Don't match Windows paths `c:\`
        if (/^[a-zA-Z]:\\/.test(url)) {
            return false;
        }

        // Scheme: https://tools.ietf.org/html/rfc3986#section-3.1
        // Absolute URL: https://tools.ietf.org/html/rfc3986#section-4.3
        return /^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(url);
    }
}

export default VNodeRenderer;
